import { useState } from "react";
import { useSearchParams } from "react-router";
import { Button, EmptyState, Icon, Notice, SetupChecklist } from "@omnabase/ui";
import { legalBehind, type ChatCounts, type Home, type HomeSetup } from "@omnabase/shared";
import { Shell } from "../shell/Shell";
import { InvitationNotices } from "./InvitationNotices";
import { HomeSkeleton } from "./HomeSkeleton";
import { useSession } from "../shell/store";
import { api } from "../lib/api";
import { useNav, useWs } from "../lib/nav";
import { ageOf, greeting, greetingName, rangeFrom, thingsNeedYou, useHome } from "../lib/home";
import { aboutTickets, useLive } from "../lib/live";
import { useCan } from "../lib/access";
import { useChatCounts } from "../lib/chat";
import { nameOf, roomPath } from "../lib/chatRooms";
import { setupRows, showSetup } from "../lib/setup";
import { AiPausedNotice } from "./HomeAiNotice";
import { HomeActivity } from "./HomeActivity";
import { HomeNotifyRow } from "./HomeNotifyRow";

/** Home's "Chat mentions" row (M10 round three, the prototype's): the unread mentions in every room the reader may see, the rooms they wait
 *  in, and a link to the room with the most; none at zero */
function chatMentionsRow(c: ChatCounts | null, members: Parameters<typeof nameOf>[0], ws: (path: string) => string): { n: number; lead?: boolean; title: string; sub: string; href: string } | null {
  if (!c?.mentions || !c.mentionRooms[0]) return null;
  return { n: c.mentions, title: c.mentions === 1 ? "Chat mention" : "Chat mentions", sub: c.mentionRooms.map((r) => (r.kind === "channel" ? `#${r.slug}` : r.kind === "ticket" ? `#${r.ticketNumber}` : nameOf(members, r.dmUserId))).join(", "), href: ws(roomPath(c.mentionRooms[0])) }; // the ticket discussion round: a ticket's discussion by its number
}

/** Home (ported from the prototype's HomeScreen, without the news part, which is M8; the suggestions line joined in M5 round six, the chat mentions row in M10 round three): one area
 *  that asks something of you, and the rest is numbers. Company-wide, whatever project is in scope elsewhere. On the first day,
 *  before any ticket has arrived, Home is the way to the first one instead of a dashboard of zeros. */
export function HomeScreen() {
  const nav = useNav(); const { ws, slug } = useWs();
  const { me, workspace } = useSession();
  const [params] = useSearchParams();
  const range = rangeFrom(params.get("range"));
  const home = useHome(slug, range.id); // the home-and-customers round: the AI tiles left Home, and with them its /ai/usage read
  useLive(slug, () => { void home.reload(); }, aboutTickets);
  const chat = useChatCounts(slug, me.user.id); // M10 round three: the "Chat mentions" row (mentions only, as the prototype; whatever the switches say since 2026-11-01)
  const now = Date.now();
  const firstName = greetingName({ workspaceName: workspace.me.names.displayName, userName: me.profile.displayName || me.user.name || workspace.members.find((m) => m.userId === me.user.id)?.name, email: me.user.email });
  const h = home.data;
  if (!h) return <Shell crumbs={[{ label: "Dashboard" }]} bodyClassName="ob-body--full"><HomeSkeleton /></Shell>;

  // M11 round two: the owner's setup checklist (the Home read's `setup`, null for everyone else): on the first day (the sample does not count
  // as a ticket) it is the whole page; afterwards a card above "Needs you" until every step is done or the owner hid it
  const setup = showSetup(h.setup) ? h.setup : null;
  if (h.totalTickets === 0) {
    return (
      <Shell crumbs={[{ label: "Dashboard" }]} bodyClassName="ob-body--full">
        <main className="ob-page ob-home ob-home--setup" aria-label="Dashboard">
          <div className="ob-home__greeting"><h1 className="ob-home__hello">Welcome, {firstName}</h1></div>
          <LegalNotice />
          <PastDueNotice />
          <InvitationNotices invitations={h.invitations} />
          {setup ? (
            <>
              <SetupCard setup={setup} onChanged={home.reload} intro="Each step ticks itself once it is really done. None is required in this order." />
              <p className="ob-home__line">The inbox, the knowledge base and team chat work from the first minute{setup.sample !== null ? "; the sample ticket shows what a draft looks like" : ""}.</p>
            </>
          ) : (
            <div className="ob-needs ob-firstrun" data-tour="next">{/* the tour's next-steps stop for everyone without the checklist */}
              <EmptyState icon="inbox" title="Nothing has arrived yet" text="Tickets land in the queue the moment the first mail or web form message comes in." />
              <div className="ob-firstrun__actions"><Button variant="primary" icon="arrowForward" onClick={(e) => nav(ws("/inbox/queue"), e as never)}>Open the inbox</Button></div>
            </div>
          )}
        </main>
      </Shell>
    );
  }

  // the one list that asks something of you; a row only exists while its number is above zero. The unassigned line is the person's own number
  // (their "Only when assigned" applies, the queue-and-chat-column round), so it opens Your queue, where exactly those tickets are
  const n = h.needsYou;
  const needs = [
    n.queue ? { n: n.queue, lead: true, title: n.queue === 1 ? "Ticket waiting on you" : "Tickets waiting on you", sub: h.oldestWaitingSince ? `Oldest has waited ${ageOf(h.oldestWaitingSince, now)}` : "", href: ws("/inbox/queue") } : null,
    chatMentionsRow(chat, workspace.members, ws),
    n.unassigned ? { n: n.unassigned, title: n.unassigned === 1 ? "Unassigned ticket" : "Unassigned tickets", sub: "Nobody has picked these up yet", href: ws("/inbox/queue") } : null,
    n.pendingOnYou ? { n: n.pendingOnYou, title: n.pendingOnYou === 1 ? "Ticket pending on you" : "Tickets pending on you", sub: "Waiting for the customer; yours when they answer", href: ws("/inbox/your-awaiting") } : null,
    // M5 round nine: the prototype's spot-check line (the replies the AI sent on its own still to check on the projects the person sees), before the suggestions as the prototype orders them
    h.spotChecks ? { n: h.spotChecks, title: h.spotChecks === 1 ? "Auto-resolved ticket to spot check" : "Auto-resolved tickets to spot check", sub: "Replies the AI sent on its own", href: ws("/inbox/auto-resolved") } : null,
    // M5 round six: the prototype's suggestions line (the open ones on the projects the person sees)
    h.suggestions ? { n: h.suggestions, title: h.suggestions === 1 ? "Knowledge base suggestion to review" : "Knowledge base suggestions to review", sub: "Questions customers keep asking that no article answers", href: ws("/knowledge") } : null,
  ].filter((x): x is NonNullable<typeof x> => !!x);
  const legalNotice = <><LegalNotice /><PastDueNotice /><InvitationNotices invitations={h.invitations} /></>;
  return (
    <Shell crumbs={[{ label: "Dashboard" }]} bodyClassName="ob-body--full">
      <main className="ob-page ob-home ob-home--split" aria-label="Dashboard">
        <div className="ob-home__cols">
          <section className="ob-home__col ob-home__col--primary" aria-label="Needs you">
            {/* the home-and-customers round: the notifications row is no need; the summary, the column head and the quiet line count the counted rows */}
            <div className="ob-home__greeting"><h1 className="ob-home__hello">{greeting(new Date(now).getHours())}, {firstName}</h1><span className="ob-home__summary">{thingsNeedYou(needs.length)}</span></div>
            {legalNotice}
            {/* M5 round eight: the prototype's notice while the customer's own cap is reached */}
            {/* M11 round two (brief choice 10 a): a self-made beta workspace starts at zero credits; its two ways to AI are the members' own AI and
                asking us for credits, beside the plan's way up */}
            {h.aiPaused && <AiPausedNotice paused={h.aiPaused} onChanged={home.reload} />}
            {setup && <SetupCard setup={setup} onChanged={home.reload} />}
            <header className="ob-home__colhead"><h2 className="ob-home__coltitle">Needs you</h2><span className="ob-home__colmeta">{needs.length || ""}</span></header>
            <div className="ob-needs" data-tour={setup ? undefined : "next"}>{/* the tour's next-steps stop when there is no checklist */}
              {needs.length === 0 ? <div className="ob-home__quiet"><Icon name="checkCircle" size="sm" />Nothing needs you right now.</div>
                : needs.map((w) => (
                  <a key={w.title} className="ob-needs__row" href={w.href} onClick={(e) => nav(w.href, e)}>
                    <span className={`ob-needs__count${w.lead ? " ob-needs__count--lead" : ""}`}>{w.n}</span>
                    <span className="ob-needs__text"><span className="ob-needs__title">{w.title}</span><span className="ob-needs__sub">{w.sub}</span></span>
                    <span className="ob-needs__go" aria-hidden="true"><Icon name="chevronRight" size="sm" /></span>
                  </a>
                ))}
              <HomeNotifyRow />
              <div className="ob-needs__foot">
                <Button variant="primary" icon="arrowForward" onClick={(e) => nav(ws("/inbox/queue"), e as never)}>{n.queue ? "Open your queue" : "Open the inbox"}</Button>
                <Button onClick={(e) => nav(ws("/inbox/all-open"), e as never)}>All open tickets<span className="ob-needs__foot-count">{h.allOpen}</span></Button>
              </div>
            </div>
          </section>
          <HomeActivity home={h} range={range} now={now} />
        </div>
      </main>
    </Shell>
  );
}

/** M11 round two: the owner's setup checklist as the kit's card; its × hides it (member settings) with Undo on the toast */
function SetupCard({ setup, intro, onChanged }: { setup: HomeSetup; intro?: string; onChanged: () => Promise<unknown> }) {
  const nav = useNav(); const { ws, slug } = useWs();
  const { toast } = useSession();
  const hide = async (dismissed: boolean) => { await api.put(`/api/v1/w/${slug}/home/setup`, { dismissed }); await onChanged(); };
  const dismiss = async () => {
    try { await hide(true); toast("Setup checklist hidden", { kind: "success", action: { label: "Undo", onClick: () => void hide(false).catch((e: unknown) => toast((e as Error).message, { kind: "error" })) } }); }
    catch (e) { toast((e as Error).message, { kind: "error" }); }
  };
  return <SetupChecklist steps={setupRows(setup).map((r) => ({ ...r, href: ws(r.to), extra: r.extra && { label: r.extra.label, href: ws(r.extra.to) } }))} onNavigate={nav} onDismiss={() => void dismiss()} intro={intro} tour="next" />;
}

/** M7 round four: while Stripe retries a failed renewal (past due) the owner is told on Home, with the way to the Billing page; the plan
 *  keeps working meanwhile. Those without the `billing` capability never see it. */
function PastDueNotice() {
  const nav = useNav(); const { ws } = useWs();
  const { workspace } = useSession();
  const can = useCan();
  if (workspace.plan.subscription.status !== "past_due" || !can("billing")) return null;
  return (
    <Notice icon="alarm" action={<Button variant="primary" size="sm" onClick={(e) => nav(ws("/settings/billing"), e as never)}>Fix the payment</Button>}>
      <strong>The {workspace.plan.name} payment failed.</strong> Stripe tries the card again over the next days and the plan keeps working meanwhile. Update the card to settle it.
    </Notice>
  );
}

/** M6 round seven: a raised version of the terms (or of the DPA on its own) asks the owner on every visit until they accept; agents never
 *  see it and nothing is blocked in the beta. One acceptance records the terms and the DPA in force. */
function LegalNotice() {
  const { workspace, reload, toast } = useSession();
  const [busy, setBusy] = useState(false);
  if (workspace.me.role !== "owner") return null;
  const termsBehind = legalBehind(workspace.terms.version, workspace.terms.current);
  const dpaBehind = legalBehind(workspace.dpa.version, workspace.dpa.current);
  if (!termsBehind && !dpaBehind) return null;
  const doc = termsBehind ? "terms" : "dpa";
  const accept = async () => {
    setBusy(true);
    try { await api.post(`/api/v1/w/${workspace.slug}/terms/accept`, { version: workspace.terms.current }); toast(termsBehind ? "Terms accepted" : "Data processing agreement accepted", { kind: "success" }); await reload(); }
    catch (e) { toast((e as Error).message, { kind: "error" }); }
    finally { setBusy(false); }
  };
  return (
    <Notice icon="article" action={<Button variant="primary" size="sm" disabled={busy} onClick={() => void accept()}>{termsBehind ? "Accept the terms" : "Accept the agreement"}</Button>}>
      <strong>{termsBehind ? `The terms of service changed (version ${workspace.terms.current}).` : `The data processing agreement changed (version ${workspace.dpa.current}).`}</strong> Read the <a className="ob-link" href={`/legal/${doc}`} target="_blank" rel="noopener noreferrer">{termsBehind ? "new terms" : "new agreement"}</a>; as the owner you accept them for the workspace. Nothing is blocked in the meantime.
    </Notice>
  );
}
